import { baseApi } from './baseQuery'
import { ServiceItem } from '@/types/interfaces/service-item'
import { apiEndpoints } from '@/lib/api/endpoints'

const VISIT_ID_KEY = 'general-ai-visit-id'
const CART_SYNC_KEY = 'general-ai-cart-sync'

interface Topic {
    sku_id: number
    sku_name: string
}

interface VisitTopicsResponse {
    visit_id: string
    duration_minutes?: number
    topics: Topic[]
}

interface VisitCartData {
    items: ServiceItem[]
    durationMinutes?: number
}

interface ApiError {
    error: string
    message?: string
}

const getVisitId = (): string | null => {
    if (typeof window === 'undefined') return null
    try {
        return localStorage.getItem(VISIT_ID_KEY)
    } catch {
        return null
    }
}

const setVisitId = (visitId: string): void => {
    if (typeof window === 'undefined') return
    try {
        localStorage.setItem(VISIT_ID_KEY, visitId)
    } catch (error) {
        console.error('Failed to save visit ID:', error)
    }
}

// Helper to trigger cross-tab sync
const triggerCartSync = (): void => {
    if (typeof window === 'undefined') return
    try {
        // Update timestamp to trigger storage event in other tabs
        localStorage.setItem(CART_SYNC_KEY, Date.now().toString())
    } catch (error) {
        console.error('Failed to trigger cart sync:', error)
    }
}

export const visitCartApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getVisitCart: builder.query<VisitCartData, void>({
            queryFn: async (_, _api, _extraOptions, baseQuery) => {
                const visitId = getVisitId()

                // No visit ID = no cart (backend is source of truth)
                if (!visitId) {
                    return { data: { items: [], durationMinutes: undefined } }
                }

                const result = await baseQuery(apiEndpoints.visits.getTopics(visitId))

                if (result.error) {
                    // Check if visit is invalid/unauthorized (booked or not accessible)
                    const errorData = result.error as any
                    if (errorData?.data?.error === 'invalid_or_unauthorized_visit') {
                        // Visit has been booked or is no longer accessible - clear it
                        setVisitId('')
                    }
                    return { data: { items: [], durationMinutes: undefined } }
                }

                const data = result.data as VisitTopicsResponse

                return {
                    data: {
                        items: data.topics.map((topic) => ({
                            skuId: String(topic.sku_id),
                            title: topic.sku_name,
                        })) as any,
                        durationMinutes: data.duration_minutes,
                    },
                }
            },
            providesTags: ['VisitCart'],
        }),
        addToCart: builder.mutation<VisitTopicsResponse, ServiceItem>({
            query: (item) => {
                const visitId = getVisitId() || undefined

                return {
                    url: apiEndpoints.visits.addTopic(visitId),
                    method: 'POST',
                    body: { sku_id: parseInt(item.skuId) },
                }
            },
            async onQueryStarted(item, { dispatch, queryFulfilled }) {
                // Optimistic update
                const patchResult = dispatch(
                    visitCartApi.util.updateQueryData('getVisitCart', undefined, (draft) => {
                        // Add item optimistically if not already there
                        if (!draft.items.some((i: any) => String(i.skuId) === String(item.skuId))) {
                            draft.items.push({ skuId: item.skuId, title: item.title } as any)
                        }
                    }),
                )
                try {
                    const { data } = await queryFulfilled
                    // Reconcile visit ID only if it changed
                    const currentVisitId = getVisitId()
                    if (currentVisitId !== data.visit_id) {
                        setVisitId(data.visit_id)
                    }
                    // Update duration in cache
                    dispatch(
                        visitCartApi.util.updateQueryData('getVisitCart', undefined, (draft) => {
                            draft.durationMinutes = data.duration_minutes
                        }),
                    )
                    // Trigger cross-tab sync
                    triggerCartSync()
                } catch {
                    // Revert optimistic update on error
                    patchResult.undo()
                }
            },
            invalidatesTags: ['VisitCart'],
        }),
        removeFromCart: builder.mutation<VisitTopicsResponse, string>({
            query: (skuId) => {
                const visitId = getVisitId() || undefined

                return {
                    url: apiEndpoints.visits.removeTopic(visitId),
                    method: 'POST',
                    body: { sku_id: parseInt(skuId) },
                }
            },
            async onQueryStarted(skuId, { dispatch, queryFulfilled }) {
                // Optimistic update
                const patchResult = dispatch(
                    visitCartApi.util.updateQueryData('getVisitCart', undefined, (draft) => {
                        // Remove item optimistically
                        const index = draft.items.findIndex((i: any) => String(i.skuId) === String(skuId))
                        if (index !== -1) {
                            draft.items.splice(index, 1)
                        }
                    }),
                )
                try {
                    const { data } = await queryFulfilled
                    // Reconcile visit ID only if it changed
                    const currentVisitId = getVisitId()
                    if (currentVisitId !== data.visit_id) {
                        setVisitId(data.visit_id)
                    }
                    // Update duration in cache
                    dispatch(
                        visitCartApi.util.updateQueryData('getVisitCart', undefined, (draft) => {
                            draft.durationMinutes = data.duration_minutes
                        }),
                    )
                    // Trigger cross-tab sync
                    triggerCartSync()
                } catch {
                    // Revert optimistic update on error
                    patchResult.undo()
                }
            },
            invalidatesTags: ['VisitCart'],
        }),
        clearCart: builder.mutation<void, void>({
            queryFn: async () => {
                if (typeof window !== 'undefined') {
                    try {
                        localStorage.removeItem(VISIT_ID_KEY)
                    } catch (error) {
                        console.error('Failed to clear cart:', error)
                    }
                }
                return { data: undefined }
            },
            invalidatesTags: ['VisitCart'],
        }),
    }),
    overrideExisting: true,
})

export const { useGetVisitCartQuery, useAddToCartMutation, useRemoveFromCartMutation, useClearCartMutation } =
    visitCartApi

export const getCurrentVisitId = (): string | null => {
    return getVisitId()
}

// Referral cart operations injected into the same cart slice
const referralCartExtension = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        addReferralToCart: builder.mutation<void, { referralId: string }>({
            query: ({ referralId }) => ({
                url: apiEndpoints.careActions.addToCart(referralId),
                method: 'POST',
            }),
            invalidatesTags: ['VisitCart'],
        }),
        removeReferralFromCart: builder.mutation<void, { referralId: string }>({
            query: ({ referralId }) => ({
                url: apiEndpoints.careActions.removeFromCart(referralId),
                method: 'POST',
            }),
            invalidatesTags: ['VisitCart'],
        }),
    }),
    overrideExisting: false,
})

export const { useAddReferralToCartMutation, useRemoveReferralFromCartMutation } =
    referralCartExtension
